import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
    UserPlus, Loader2, Check, Copy, Mail, Phone, User,
    ArrowRight, Sparkles, ShieldCheck,
} from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { adminMembersApi } from "@/lib/api/adminMembers";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const TIERS = [
    { value: "SILVER",   en: "Silver",   ar: "فضي",   sub_en: "Standard tier",   sub_ar: "الباقة الأساسية" },
    { value: "GOLD",     en: "Gold",     ar: "ذهبي",  sub_en: "Premium tier",    sub_ar: "الباقة المميزة"   },
    { value: "PLATINUM", en: "Platinum", ar: "بلاتيني",sub_en: "Top tier",       sub_ar: "أعلى باقة"        },
];

const EMPTY_FORM = {
    full_name: "", email: "", phone: "",
    tier: "SILVER", expiry_months: 12,
    password: "",
};

export default function KioskRegisterPage() {
    const { lang } = useI18n();
    const tr = (en, ar) => lang === "ar" ? ar : en;

    const [form, setForm] = useState(EMPTY_FORM);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [result, setResult] = useState(null);
    const [copied, setCopied] = useState(false);

    const update = (patch) => setForm((p) => ({ ...p, ...patch }));

    const submit = async (e) => {
        e.preventDefault();
        setError(""); setSubmitting(true);
        try {
            const payload = {
                full_name: form.full_name.trim(),
                email: form.email.trim().toLowerCase(),
                phone: form.phone.trim() || null,
                tier: form.tier,
                expiry_months: Number(form.expiry_months) || 12,
            };
            if (form.password.trim()) payload.password = form.password.trim();
            const res = await adminMembersApi.create(payload);
            setResult(res);
            toast.success(tr("Member registered", "تم تسجيل العضو"));
        } catch (err) {
            setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        } finally { setSubmitting(false); }
    };

    const reset = () => {
        setForm(EMPTY_FORM);
        setResult(null);
        setError("");
        setCopied(false);
    };

    const copyDetails = () => {
        if (!result) return;
        const lines = [
            `Hemaia Member Card`,
            `Name: ${result.full_name}`,
            `Member Code: ${result.member_code}`,
            `Tier: ${result.tier}`,
            `Expiry: ${result.expiry_date}`,
        ];
        if (result.temporary_password) lines.push(`Temp password: ${result.temporary_password}`);
        navigator.clipboard.writeText(lines.join("\n")).then(() => {
            setCopied(true);
            toast.success(tr("Copied to clipboard", "تم النسخ"));
            setTimeout(() => setCopied(false), 2500);
        });
    };

    // ── Success state ────────────────────────────────────────────────────────
    if (result) {
        return (
            <div className="max-w-xl space-y-6 fade-up" data-testid="kiosk-register-success">
                <header>
                    <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full mb-3">
                        <Check className="h-3.5 w-3.5" />
                        {tr("Member registered", "تم تسجيل العضو")}
                    </div>
                    <h1 className="text-3xl font-semibold tracking-tight">
                        {tr("All set, ", "تم بنجاح، ")}{result.full_name?.split(" ")[0]}!
                    </h1>
                    <p className="mt-1.5 text-sm text-muted-foreground">
                        {tr(
                            "Share these details with the new member.",
                            "شارك هذه التفاصيل مع العضو الجديد."
                        )}
                    </p>
                </header>

                {/* Card */}
                <div className="rounded-2xl border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10 p-6 space-y-4">
                    <div className="flex items-start justify-between">
                        <div>
                            <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">
                                {tr("Member Code", "كود العضوية")}
                            </p>
                            <p className="mt-1 text-2xl font-bold font-mono tracking-wider" data-testid="result-member-code">
                                {result.member_code}
                            </p>
                        </div>
                        <button
                            onClick={copyDetails}
                            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-card border border-border text-xs font-semibold hover:bg-secondary"
                        >
                            {copied
                                ? <><Check className="h-3.5 w-3.5 text-emerald-600" />{tr("Copied", "تم النسخ")}</>
                                : <><Copy className="h-3.5 w-3.5" />{tr("Copy", "نسخ")}</>
                            }
                        </button>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">{tr("Tier", "المستوى")}</p>
                            <p className="text-sm font-semibold mt-0.5">{result.tier}</p>
                        </div>
                        <div>
                            <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">{tr("Expires", "تنتهي في")}</p>
                            <p className="text-sm font-semibold mt-0.5">{result.expiry_date}</p>
                        </div>
                    </div>

                    {result.temporary_password && (
                        <div className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2.5">
                            <p className="text-[11px] uppercase tracking-wide text-amber-700 font-bold">
                                {tr("Temporary password", "كلمة مرور مؤقتة")}
                            </p>
                            <p className="font-mono font-semibold text-amber-900 mt-0.5" data-testid="result-temp-password">
                                {result.temporary_password}
                            </p>
                            <p className="text-[11px] text-amber-700 mt-1">
                                {tr(
                                    "The member should change this on first login.",
                                    "يجب على العضو تغييرها عند أول تسجيل دخول."
                                )}
                            </p>
                        </div>
                    )}
                </div>

                <div className="flex flex-wrap gap-2">
                    <button
                        onClick={reset}
                        className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-primary text-primary-foreground font-semibold text-sm hover:opacity-90"
                        data-testid="register-another"
                    >
                        <UserPlus className="h-4 w-4" />
                        {tr("Register another member", "تسجيل عضو آخر")}
                    </button>
                    <Link
                        to="/kiosk/sales"
                        className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-secondary text-foreground font-semibold text-sm hover:bg-secondary/80"
                    >
                        {tr("View my sales", "عرض مبيعاتي")}
                        <ArrowRight className="h-4 w-4" />
                    </Link>
                </div>
            </div>
        );
    }

    // ── Form ─────────────────────────────────────────────────────────────────
    return (
        <div className="max-w-xl space-y-6 fade-up" data-testid="page-kiosk-register">
            <header>
                <p className="text-sm font-medium text-primary">{tr("Sales Kiosk", "كشك المبيعات")}</p>
                <h1 className="mt-1 text-3xl font-semibold tracking-tight">
                    {tr("Register a new member", "تسجيل عضو جديد")}
                </h1>
                <p className="mt-1.5 text-sm text-muted-foreground">
                    {tr(
                        "Quick sign-up — the member will be attributed to you automatically.",
                        "تسجيل سريع — سيُنسب العضو إليك تلقائياً."
                    )}
                </p>
            </header>

            <form onSubmit={submit} className="space-y-4 surface-card p-6">
                {/* Full name */}
                <div>
                    <label className="block text-sm font-medium mb-1.5">
                        {tr("Full name", "الاسم الكامل")}
                    </label>
                    <div className="relative">
                        <User className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                        <input
                            type="text"
                            required
                            autoFocus
                            value={form.full_name}
                            onChange={(e) => update({ full_name: e.target.value })}
                            placeholder={tr("e.g. Ahmed Hassan", "مثال: أحمد حسن")}
                            className="w-full ps-10 pe-3 py-2.5 rounded-xl border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                            data-testid="kiosk-full-name"
                        />
                    </div>
                </div>

                {/* Email */}
                <div>
                    <label className="block text-sm font-medium mb-1.5">
                        {tr("Email", "البريد الإلكتروني")}
                    </label>
                    <div className="relative">
                        <Mail className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                        <input
                            type="email"
                            required
                            dir="ltr"
                            value={form.email}
                            onChange={(e) => update({ email: e.target.value })}
                            placeholder="member@example.com"
                            className="w-full ps-10 pe-3 py-2.5 rounded-xl border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                            data-testid="kiosk-email"
                        />
                    </div>
                </div>

                {/* Phone (optional) */}
                <div>
                    <label className="block text-sm font-medium mb-1.5">
                        {tr("Phone", "رقم الهاتف")}
                        <span className="ms-1.5 text-xs text-muted-foreground font-normal">
                            ({tr("optional", "اختياري")})
                        </span>
                    </label>
                    <div className="relative">
                        <Phone className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                        <input
                            type="tel"
                            dir="ltr"
                            value={form.phone}
                            onChange={(e) => update({ phone: e.target.value })}
                            placeholder="+20…"
                            className="w-full ps-10 pe-3 py-2.5 rounded-xl border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                            data-testid="kiosk-phone"
                        />
                    </div>
                </div>

                {/* Tier */}
                <div>
                    <label className="block text-sm font-medium mb-2">
                        {tr("Membership tier", "مستوى العضوية")}
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                        {TIERS.map((t) => {
                            const isOn = form.tier === t.value;
                            return (
                                <button
                                    type="button"
                                    key={t.value}
                                    onClick={() => update({ tier: t.value })}
                                    className={`text-start rounded-xl border-2 px-3 py-3 transition-all ${
                                        isOn
                                            ? "border-primary bg-primary/5 shadow-sm"
                                            : "border-border bg-card hover:border-primary/30"
                                    }`}
                                    data-testid={`kiosk-tier-${t.value}`}
                                >
                                    <p className="text-sm font-bold">{lang === "ar" ? t.ar : t.en}</p>
                                    <p className="text-[11px] text-muted-foreground mt-0.5">
                                        {lang === "ar" ? t.sub_ar : t.sub_en}
                                    </p>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Expiry months */}
                <div>
                    <label className="block text-sm font-medium mb-1.5">
                        {tr("Validity (months)", "المدة (بالشهور)")}
                    </label>
                    <select
                        value={form.expiry_months}
                        onChange={(e) => update({ expiry_months: e.target.value })}
                        className="w-full px-3 py-2.5 rounded-xl border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                        data-testid="kiosk-expiry-months"
                    >
                        <option value={3}>{tr("3 months", "٣ شهور")}</option>
                        <option value={6}>{tr("6 months", "٦ شهور")}</option>
                        <option value={12}>{tr("12 months", "١٢ شهر")}</option>
                        <option value={24}>{tr("24 months", "٢٤ شهر")}</option>
                    </select>
                </div>

                {/* Password (optional) */}
                <div>
                    <label className="block text-sm font-medium mb-1.5">
                        {tr("Password", "كلمة المرور")}
                        <span className="ms-1.5 text-xs text-muted-foreground font-normal">
                            ({tr("leave empty to auto-generate", "اتركه فارغاً للتوليد تلقائياً")})
                        </span>
                    </label>
                    <input
                        type="text"
                        dir="ltr"
                        value={form.password}
                        onChange={(e) => update({ password: e.target.value })}
                        placeholder={tr("Auto-generated", "يُولّد تلقائياً")}
                        className="w-full px-3 py-2.5 rounded-xl border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary font-mono"
                        data-testid="kiosk-password"
                    />
                </div>

                {error && (
                    <div className="rounded-xl border border-destructive/30 bg-destructive/5 px-3 py-2.5 text-sm text-destructive">
                        {error}
                    </div>
                )}

                <button
                    type="submit"
                    disabled={submitting}
                    className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-xl bg-primary text-primary-foreground font-semibold text-sm hover:opacity-90 disabled:opacity-50"
                    data-testid="kiosk-register-submit"
                >
                    {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                    {!submitting && <UserPlus className="h-4 w-4" />}
                    {tr("Register member", "تسجيل العضو")}
                </button>
            </form>

            {/* Helper card */}
            <div className="rounded-xl border border-border/50 bg-muted/30 p-4 flex items-start gap-3">
                <ShieldCheck className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                <div>
                    <p className="text-xs font-semibold text-foreground">
                        {tr("Attribution & commission", "النسب والعمولة")}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                        {tr(
                            "Every member you register here is attributed to you. Commission is tracked automatically.",
                            "كل عضو تسجله هنا يُنسب إليك. العمولة تُحسب تلقائياً."
                        )}
                    </p>
                </div>
            </div>
        </div>
    );
}
